import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme/ThemeContext';
import './ToothSelector.css';

interface ToothSelectorProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

type Dentition = 'permanent' | 'primary';

const PERMANENT_UPPER = ['18', '17', '16', '15', '14', '13', '12', '11', '21', '22', '23', '24', '25', '26', '27', '28'];
const PERMANENT_LOWER = ['48', '47', '46', '45', '44', '43', '42', '41', '31', '32', '33', '34', '35', '36', '37', '38'];
const PRIMARY_UPPER = ['55', '54', '53', '52', '51', '61', '62', '63', '64', '65'];
const PRIMARY_LOWER = ['85', '84', '83', '82', '81', '71', '72', '73', '74', '75'];

export const parseTreatedTeeth = (value?: string | null): string[] => {
  if (!value) return [];
  return Array.from(new Set(value.split(/[\s,;]+/).map(item => item.trim()).filter(Boolean)));
};

const toothPosition = (index: number, count: number, upper: boolean) => {
  const spacing = count === 16 ? 38 : 42;
  const totalWidth = (count - 1) * spacing;
  const x = (660 - totalWidth) / 2 - 15 + index * spacing;
  const distanceFromCenter = Math.abs(index - (count - 1) / 2);
  const archDepth = ((count - 1) / 2 - distanceFromCenter) * (count === 16 ? 3 : 4);
  const y = upper ? 19 + archDepth : 157 - archDepth;
  return { x, y };
};

export const ToothSelector: React.FC<ToothSelectorProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const selected = useMemo(() => new Set(value), [value]);
  const onlyPrimarySelected = value.length > 0 && value.every(item => Number(item) >= 50);
  const [dentition, setDentition] = useState<Dentition>(onlyPrimarySelected ? 'primary' : 'permanent');

  useEffect(() => {
    if (onlyPrimarySelected) setDentition('primary');
  }, [onlyPrimarySelected]);

  const upper = dentition === 'permanent' ? PERMANENT_UPPER : PRIMARY_UPPER;
  const lower = dentition === 'permanent' ? PERMANENT_LOWER : PRIMARY_LOWER;

  const toggle = (tooth: string) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(tooth)) next.delete(tooth);
    else next.add(tooth);
    onChange(Array.from(next).sort((a, b) => Number(a) - Number(b)));
  };

  const renderTooth = (tooth: string, index: number, row: string[], upperArch: boolean) => {
    const { x, y } = toothPosition(index, row.length, upperArch);
    const isSelected = selected.has(tooth);
    const toothLabel = t('calendar.toothSelector.toothLabel', 'Tooth {{number}}', { number: tooth });
    return (
      <g
        key={tooth}
        className={`tooth-selector-tooth${isSelected ? ' is-selected' : ''}`}
        transform={`translate(${x} ${y})${upperArch ? ' translate(0 42) scale(1 -1)' : ''}`}
        role="checkbox"
        aria-label={toothLabel}
        aria-checked={isSelected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => toggle(tooth)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle(tooth);
          }
        }}
      >
        <rect className="tooth-selector-hit-area" x="-3" y="-4" width="36" height="57" rx="8" />
        <path
          d="M5 4 Q15 -1 25 4 L28 15 Q27 21 23 24 L21 36 Q20 42 15 42 Q10 42 9 36 L7 24 Q3 21 2 15 Z"
          fill={isSelected ? tokens.app.primary : tokens.app.input}
          stroke={isSelected ? tokens.app.primary : tokens.app.inputBorder}
        />
        <text
          className="tooth-selector-number"
          x="15"
          y={upperArch ? -7 : 53}
          transform={upperArch ? 'scale(1 -1) translate(0 14)' : undefined}
          fill={isSelected ? tokens.app.primary : tokens.app.muted}
        >
          {tooth}
        </text>
      </g>
    );
  };

  return (
    <section className="tooth-selector" style={{ borderColor: tokens.app.border, background: tokens.app.surfaceAlt }}>
      <div className="tooth-selector-header">
        <div>
          <strong>{t('calendar.modal.treatedTeeth', 'Treated teeth')}</strong>
          <div className="tooth-selector-help" style={{ color: tokens.app.muted }}>
            {t('calendar.toothSelector.help', 'Select every tooth treated during this appointment (FDI notation).')}
          </div>
        </div>
        <div className="tooth-selector-actions">
          <div className="tooth-selector-tabs" role="tablist" aria-label={t('calendar.toothSelector.dentition', 'Dentition')}>
            {(['permanent', 'primary'] as Dentition[]).map(option => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={dentition === option}
                className={dentition === option ? 'is-active' : ''}
                style={{
                  borderColor: tokens.app.border,
                  background: dentition === option ? tokens.app.primary : tokens.app.input,
                  color: dentition === option ? tokens.app.primaryText : tokens.app.text
                }}
                onClick={() => setDentition(option)}
              >
                {t(`calendar.toothSelector.${option}`, option === 'permanent' ? 'Adult' : 'Primary')}
              </button>
            ))}
          </div>
          {value.length > 0 && !disabled && (
            <button type="button" className="tooth-selector-clear" style={{ color: tokens.app.primary }} onClick={() => onChange([])}>
              {t('calendar.toothSelector.clear', 'Clear')}
            </button>
          )}
        </div>
      </div>

      <svg
        className="tooth-selector-chart"
        viewBox="0 0 660 220"
        role="group"
        aria-label={t('calendar.toothSelector.chartLabel', 'Interactive dental chart')}
      >
        <line x1="330" y1="8" x2="330" y2="210" stroke={tokens.app.border} strokeDasharray="4 5" />
        <line x1="25" y1="110" x2="635" y2="110" stroke={tokens.app.border} strokeDasharray="4 5" />
        <text x="18" y="104" fill={tokens.app.muted} className="tooth-selector-orientation">{t('calendar.toothSelector.patientRight', 'Patient right')}</text>
        <text x="642" y="104" textAnchor="end" fill={tokens.app.muted} className="tooth-selector-orientation">{t('calendar.toothSelector.patientLeft', 'Patient left')}</text>
        {upper.map((tooth, index) => renderTooth(tooth, index, upper, true))}
        {lower.map((tooth, index) => renderTooth(tooth, index, lower, false))}
      </svg>

      <div className="tooth-selector-selection" style={{ color: value.length ? tokens.app.text : tokens.app.muted }} aria-live="polite">
        {value.length
          ? t('calendar.toothSelector.selected', 'Selected: {{teeth}}', { teeth: value.join(', ') })
          : t('calendar.toothSelector.noneSelected', 'No teeth selected')}
      </div>
    </section>
  );
};
